import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartTooltip, formatEuro, formatEuroAchse, formatMonat } from '@elbklar/ui';

import type { ProjectionSeries } from '@/types';

interface RunwayChartProps {
  base: ProjectionSeries;
  scenario?: ProjectionSeries | null;
  minBuffer: string;
  height?: number;
}

interface Punkt {
  label: string;
  basis: number;
  szenario?: number;
  /** Untergrenze und Hoehe des Bandes zwischen Basis und Szenario. */
  bandBasis?: number;
  bandHoehe?: number;
}

/**
 * Kontoverlauf ueber die Zeit.
 *
 * Die Pufferlinie ist bewusst prominent: der Engpass beginnt dort, nicht erst
 * bei null. Die Szenario-Linie liegt als gestrichelte Kurve darueber, damit
 * der Unterschied auf einen Blick sichtbar ist.
 *
 * Farben nach U9 (Auftrag 010): Basis `chart-1`, Szenario und das Band
 * dazwischen `chart-2`, Puffer gestrichelt in `warning` (er ist die
 * Warnschwelle), Nulllinie duenn und Kipppunkte in `danger`. Alles ueber die
 * CSS-Variablen des Themes, damit hell und dunkel stimmen.
 */
export function RunwayChart({
  base,
  scenario,
  minBuffer,
  height = 320,
}: RunwayChartProps) {
  const puffer = Number(minBuffer);

  const daten: Punkt[] = base.points.map((punkt, index) => {
    const basis = Number(punkt.closing);
    const szenario = scenario
      ? Number(scenario.points[index]?.closing)
      : undefined;

    // Das Band zwischen beiden Linien IST der Unterschied — als Flaeche
    // gezeichnet ist er greifbarer als zwei Linien, die man vergleichen muss.
    // Recharts stapelt Areas, deshalb Untergrenze + Hoehe statt zweier Werte.
    const band =
      szenario === undefined || Number.isNaN(szenario)
        ? {}
        : {
            bandBasis: Math.min(basis, szenario),
            bandHoehe: Math.abs(basis - szenario),
          };

    return { label: punkt.label, basis, szenario, ...band };
  });

  /** Monatslabel, in dem der Saldo erstmals negativ wird. */
  const kipppunkt = (serie: ProjectionSeries | null | undefined) =>
    serie?.points.find((p) => p.negative)?.label;

  const kippBasis = kipppunkt(base);
  const kippSzenario = scenario ? kipppunkt(scenario) : undefined;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={daten} margin={{ top: 16, right: 8, bottom: 0, left: 8 }}>
        <defs>
          <linearGradient id="verlauf" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--chart-1)', stopOpacity: 0.18 }} />
            <stop offset="100%" style={{ stopColor: 'var(--chart-1)', stopOpacity: 0.01 }} />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="label"
          tickFormatter={(m: string) => formatMonat(m, { kurz: true })}
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={{ stroke: 'var(--border)' }}
          tickLine={false}
          interval="preserveStartEnd"
          minTickGap={24}
        />
        <YAxis
          tickFormatter={formatEuroAchse}
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={false}
          tickLine={false}
          width={56}
        />

        {/* Nulllinie: ab hier ist das Konto leer. */}
        <ReferenceLine y={0} stroke="var(--danger)" strokeWidth={1} />

        {/* Mindestpuffer: die eigentliche Warnschwelle. */}
        {puffer > 0 && (
          <ReferenceLine
            y={puffer}
            stroke="var(--warning)"
            strokeDasharray="4 4"
            strokeWidth={1}
            label={{
              value: `Puffer ${formatEuroAchse(puffer)}`,
              position: 'insideTopRight',
              fontSize: 10,
              fill: 'var(--warning)',
            }}
          />
        )}

        {/* Kipppunkte: der Monat, in dem das Konto ins Minus laeuft. */}
        {kippBasis && (
          <ReferenceLine
            x={kippBasis}
            stroke="var(--danger)"
            strokeWidth={1}
            strokeDasharray="2 3"
            label={{
              value: formatMonat(kippBasis, { kurz: true }),
              position: 'top',
              fontSize: 10,
              fill: 'var(--danger)',
            }}
          />
        )}
        {kippSzenario && kippSzenario !== kippBasis && (
          <ReferenceLine
            x={kippSzenario}
            stroke="var(--danger)"
            strokeWidth={1}
            strokeDasharray="2 3"
            label={{
              value: formatMonat(kippSzenario, { kurz: true }),
              position: 'top',
              fontSize: 10,
              fill: 'var(--danger)',
            }}
          />
        )}

        <Tooltip
          cursor={{ stroke: 'var(--border)' }}
          content={
            <ChartTooltip<Punkt>
              titel={(punkt) => formatMonat(punkt.label)}
              zeilen={(punkt) => [
                { label: 'Basis', value: formatEuro(punkt.basis, { cent: true }) },
                ...(punkt.szenario === undefined || Number.isNaN(punkt.szenario)
                  ? []
                  : [{ label: 'Mit Szenario', value: formatEuro(punkt.szenario, { cent: true }) }]),
              ]}
            />
          }
        />

        <Area
          type="monotone"
          dataKey="basis"
          stroke="var(--chart-1)"
          strokeWidth={2}
          fill="url(#verlauf)"
          dot={false}
        />

        {/* Das Band zwischen den Linien: zwei gestapelte Flaechen, von denen
            die untere unsichtbar bleibt — so faerbt sich genau der Abstand. */}
        {scenario && (
          <>
            <Area
              type="monotone"
              dataKey="bandBasis"
              stackId="band"
              stroke="none"
              fill="none"
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
            />
            <Area
              type="monotone"
              dataKey="bandHoehe"
              stackId="band"
              stroke="none"
              fill="var(--chart-2)"
              fillOpacity={0.16}
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
            />
            <Line
              type="monotone"
              dataKey="szenario"
              stroke="var(--chart-2)"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
            />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
